import type { DynamicTag } from "@/api/useWorkflow.types"
import {
  DatePicker,
  FormInstance,
  FormItemProps,
  Input,
  InputNumber,
  Select,
  TextArea
} from "@/atoms"
import {
  isDateField,
  isMultipleFetchableStringField,
  isMultiplePredefinedOptionsField,
  isNumberField,
  isSingleFetchableOptionsField,
  isSinglePredefinedOptionsField,
  isSprintField,
  isTextAreaField,
  isTextField
} from "@/helpers/jira.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import { toSelectionOption } from "@/molecules/JiraIssueField/helpers"
import { DynamicTagsFormItem } from "@/organisms/Ticketing/components/DynamicTagsField"
import React from "react"
import {
  AutoCompleteField,
  MultiSelectFetchableField,
  OnDemandDateField,
  SprintRadioGroup
} from "./fields"
import { JiraFormItem } from "./JiraFormItem"
import styles from "./JiraIssueField.module.scss"

import type { JiraTemplateFormValues } from "@/organisms/Ticketing/components/JiraTemplateModal/JiraTemplateModal.types"

const JiraDynamicField = ({
  field,
  className,
  label,
  projectId,
  rules,
  disabled,
  tags,
  form,
  previewMode,
  existingTemplate,
  mode = "ticket"
}: {
  field: Jira.IssueTypeField
  className?: string
  label?: React.ReactNode
  projectId: string
  mode?: "ticket" | "template"
  rules?: FormItemProps["rules"]
  disabled?: boolean
  tags?: DynamicTag[]
  form?: FormInstance
  existingTemplate?: JiraTemplateFormValues
  previewMode?: boolean
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: mode === "template" ? "jiraTemplateModal" : "jiraIssueModal"
  })

  let renderField = null

  // TEMPLATE MODE: Special handling for sprint and date fields
  if (mode === "template" && isSprintField(field)) {
    return (
      <SprintRadioGroup
        disabled={disabled}
        fieldKey={field.key}
        className={className}
        label={label}
        required={field.required}
        fieldName={field.name}
        formItemPrefix={"onDemandFields"}
        t={t}
      />
    )
  } else if (mode === "template" && isDateField(field)) {
    return (
      <OnDemandDateField
        disabled={disabled}
        fieldKey={field.key}
        className={className}
        label={label}
        required={field.required}
        fieldName={field.name}
        formItemPrefix={"onDemandFields"}
        t={t}
      />
    )
  } else if (isSinglePredefinedOptionsField(field)) {
    renderField = (
      <Select
        disabled={disabled}
        options={field.allowedValues.map(toSelectionOption)}
        allowClear
      />
    )
  } else if (isMultipleFetchableStringField(field)) {
    renderField = (
      <MultiSelectFetchableField
        disabled={disabled}
        field={field}
        projectId={projectId}
      />
    )
  } else if (isSingleFetchableOptionsField(field)) {
    renderField = (
      <AutoCompleteField
        disabled={disabled}
        field={field}
        projectId={projectId}
        allowClear
      />
    )
  } else if (isMultiplePredefinedOptionsField(field)) {
    renderField = (
      <Select
        disabled={disabled}
        mode={"multiple"}
        options={field.allowedValues.map(toSelectionOption)}
        allowClear
      />
    )
  } else if (isTextAreaField(field)) {
    renderField = <TextArea disabled={disabled} allowClear />
  } else if (isTextField(field)) {
    if (mode === "template" && form)
      return (
        <DynamicTagsFormItem
          disabled={disabled}
          previewMode={previewMode}
          form={form}
          label={label}
          name={field.key}
          rules={rules}
          tags={tags}
          initialValue={`${existingTemplate?.[field.key] || ""}`}
          type="input"
        />
      )

    renderField = <Input allowClear disabled={disabled} />
  } else if (isNumberField(field)) {
    renderField = (
      <InputNumber disabled={disabled} className={styles.inputNumber} />
    )
  } else if (isDateField(field)) {
    renderField = <DatePicker disabled={disabled} />
  }

  if (!renderField) return null

  return (
    <JiraFormItem
      fieldKey={field.key}
      className={className}
      label={label}
      required={field.required}
      formItemName={field.key}
      rules={rules}
    >
      {renderField}
    </JiraFormItem>
  )
}

export default JiraDynamicField
